import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { Darkroom, PrintOnPeg } from '../components/Reveal';
import { StoryOpen, StoryReveal } from '../components/StoryPrint';
import { getStoryboard, askForStoryboard, storyOpened, markStoryOpened, storyWords, WORKING, type Storyboard } from '../lib/storyboard';
import { transition } from '../lib/transition';
import { track } from '../analytics';
import { SendStory, FRIEND_STORY } from '../components/SendStory';
import { friendOfStory } from '../lib/friend-stories';
import { CastAsk, CastFlow } from '../components/Cast';
import { castDry, declineCast, shouldAsk } from '../lib/cast';
import { loadProfile } from '../lib/state';
import { useViewed } from '../lib/viewed';

/**
 * /story/:id — your story, storyboarded (well/research/16, C1). The same
 * darkroom and the same print as every exercise's picture: developing while
 * the crew work, face-down on the peg once it is dry, turned over by your tap
 * into the page, which plays back read by the narrator. `id` is the story's,
 * or the post's it was told with.
 *
 * Be in your story (well/product/32): opened for the first time (or, after one
 * "Not now", on a later day), the way on is "Want to be in it?"; a recast
 * still drawing shows its darkroom, and a dry one hangs gold on the peg.
 */
export function Story() {
  const { postId: id = '' } = useParams();
  const nav = useNavigate();
  const [sb, setSb] = useState<Storyboard | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [turned, setTurned] = useState<'no' | 'turning' | 'open'>(() => (storyOpened(id) ? 'open' : 'no'));
  // the cast: asked about on this visit (fixed once known, so a "Not now" does not re-ask), or under way
  const [cast, setCast] = useState<'idle' | 'tag' | 'flow' | 'done'>('idle');
  useViewed('story', cast === 'idle' ? turned : `cast_${cast}`);
  const [asking, setAsking] = useState<boolean | null>(null);

  const poll = (tries = 0) => {
    getStoryboard(id).then((s) => {
      setSb(s);
      if (s && WORKING.includes(s.status) && tries < 80) setTimeout(() => poll(tries + 1), 3000);
    }).catch(() => { if (tries < 80) setTimeout(() => poll(tries + 1), 3000); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { poll(); }, [id]);
  useEffect(() => { if (sb && storyOpened(sb.storyId)) setTurned('open'); }, [sb]);
  useEffect(() => { if (sb && asking === null && sb.status === 'done') setAsking(shouldAsk(sb, loadProfile())); }, [sb, asking]);

  const ask = async (again: boolean) => {
    setErr(null);
    try { setSb(await askForStoryboard(id, again)); track('storyboard_asked', { again }); setTimeout(() => poll(), 2000); }
    catch (e) {
      const code = (e as { code?: string }).code;
      setErr(code === 'crew_off' ? 'Your crew is off, so nothing reads your story. You can turn them on from Friends.' : code === 'no_story' ? 'There is no told story on this one to storyboard.' : 'Could not ask the crew just now.');
    }
  };
  // back where it was opened from: Home, or the road (whose stop then pops as its storyboard is seen)
  const from = useLocation().state as { back?: string; landed?: string } | null;
  const home = () => nav(from?.back ?? '/', { replace: true, viewTransition: true, state: from?.landed ? { landed: from.landed } : undefined });

  if (sb === undefined) return <div className="stage" style={{ background: '#2b2a44' }} />;

  if (sb === null || sb.status === 'failed') {
    const blocked = sb?.blocked === 'billing';
    return (
      <Frame onBack={home} left={<span className="tag">Your story</span>}>
        <h1>{sb ? (blocked ? 'Your crew are out of ink just now.' : 'The storyboard did not come out.') : 'No storyboard yet.'}</h1>
        <p className="lede">{sb ? (blocked ? 'Your story is kept. Ask again later and they will draw it.' : 'Your story is kept. The crew can have another go.') : 'The crew can draw the story you told as four panels, in your own words.'}</p>
        {err && <p className="hint">{err}</p>}
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={() => ask(!!sb)}>{sb ? 'Try again' : 'Storyboard my story'}</button>
        </div>
      </Frame>
    );
  }

  if (WORKING.includes(sb.status)) return <Darkroom day={sb.day} words={storyWords(sb)} onLeave={home} />;

  // you, being drawn into it: from the name tag, the darkroom, or the gold print on the peg
  if (cast === 'tag') return <CastFlow sb={sb} start="tag" onDone={home} />;
  if (cast !== 'done' && (cast === 'flow' || sb.cast?.status === 'casting' || castDry(sb))) {
    return <CastFlow sb={sb} start={castDry(sb) ? 'ready' : 'casting'} wipeFrom={sb.beforePageUrl} onDone={() => { setCast('done'); home(); }} />;
  }

  // Too short to break into four: the typed-out story (C5), their words and nothing else.
  if (sb.status === 'too-short') {
    return (
      <Frame onBack={home} left={<span className="tag">Day {sb.day} · your story</span>}>
        <span className="label">Your story, typed out</span>
        <div className="sb-typed">{sb.transcript || '…'}</div>
        <p className="hint">{sb.error ? 'A little short for four panels, but the crew can split a sentence in two. Your story is kept.' : 'A little short for four panels. Tell a longer one and the crew will storyboard it.'}</p>
        {/* cut short at the plan (sb.error), not at the word count: the crew split a short story now, so another go can land */}
        {sb.error && (
          <>
            {err && <p className="hint">{err}</p>}
            <div className="stack" style={{ marginTop: 'auto' }}>
              <button className="btn" onClick={() => ask(true)}>Have another go</button>
            </div>
          </>
        )}
      </Frame>
    );
  }

  if (turned === 'no') {
    return <PrintOnPeg day={sb.day} words={storyWords(sb)} onTurn={() => { track('print_turned', { from: 'story', of: 'story' }); markStoryOpened(sb.storyId); setTurned('turning'); }} onLater={home} />;
  }
  // a story about you two, opened from Home after the darkroom was left: the send is still on it
  const postId = sb.postId ?? sb.storyId;
  const friend = sb.challengeId === FRIEND_STORY ? friendOfStory(postId)?.name : undefined;
  const send = sb.challengeId === FRIEND_STORY ? { extra: <SendStory postId={postId} friend={friend} />, kicker: `Storyboarded by the Moon · you and ${friend || 'a friend'}` } : {};
  const castAsk = asking && cast === 'idle'
    ? <CastAsk onYes={() => setCast('tag')} onNo={() => { declineCast(); setAsking(false); }} />
    : undefined;
  if (turned === 'turning') return <StoryReveal sb={sb} onDone={home} doneLabel="Done" ask={castAsk} {...send} />;
  return <StoryOpen sb={sb} onDone={() => transition(home)} doneLabel="Done" ask={castAsk} {...send} />;
}
